import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CallLog } from "@/models/CallLog";
import { format } from "date-fns";
import { FileText, Play, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "../../../supabase/supabase";
import TranscriptDialog from "../callLogs/TranscriptDialog";
import { PhoneNumberDisplay } from "@/components/ui/phone-number-display";
import { AudioPlayer } from "@/components/ui/audio-player";
import { VideoPlayer } from "@/components/ui/video-player";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface CampaignSpecificCallLogsListProps {
  campaignId: string;
  organizationId: string;
}

export const CampaignSpecificCallLogsList = ({
  campaignId,
  organizationId,
}: CampaignSpecificCallLogsListProps) => {
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTranscript, setSelectedTranscript] = useState<{
    transcript: any[] | null;
    summary?: string | null;
    callDate: string;
    callInfo: {
      phoneNumber?: string;
      direction?: string;
      duration?: number;
    };
  } | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<{
    url: string;
    callDate?: string;
    phoneNumber?: string;
    isVideo?: boolean;
  } | null>(null);

  useEffect(() => {
    fetchCallLogs();
  }, [campaignId]);

  const fetchCallLogs = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("call_logs")
        .select(`
          *,
          assistants(name),
          leads(*),
          summary
        `)
        .eq("campaign_id", campaignId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      console.log("Campaign call logs fetched:", data?.length || 0);
      setCallLogs(data || []);
    } catch (error) {
      console.error("Error fetching call logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const openMediaPlayer = (url: string, log: CallLog) => {
    if (!url) return;
    
    // Get phone number from various possible fields
    const phoneNumber = 
      (log.call_type === "inbound" ? log.caller_number : log.called_number) ||
      log.to_number || 
      log.from_number ||
      '';
    
    const urlPath = url.split("?")[0].toLowerCase();
    const isVideo = urlPath.endsWith('.mp4') || urlPath.endsWith('.webm');
    
    setSelectedMedia({
      url,
      callDate: log.created_at,
      phoneNumber,
      isVideo
    });
  };

  const viewTranscript = (log: CallLog) => {
    // Get phone number from various possible fields
    const phoneNumber = 
      (log.call_type === "inbound" ? log.caller_number : log.called_number) ||
      log.to_number || 
      log.from_number ||
      '';
    
    setSelectedTranscript({
      transcript: log.transcript ? JSON.parse(log.transcript) : [null],
      summary: log.summary,
      callDate: log.created_at,
      callInfo: {
        phoneNumber: phoneNumber || undefined,
        direction: log.call_type,
        duration: log.call_duration || undefined,
      },
    });
  };

  return (
    <div className="overflow-x-auto">
      <div className="flex justify-between items-center mb-6">
        <Button onClick={fetchCallLogs} disabled={loading}>
          <RefreshCw
            className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
          />
          {loading ? "Loading..." : "Refresh"}
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Phone Number</TableHead>
            <TableHead>Direction</TableHead>
            <TableHead>Assistant</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Ended Reason</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {callLogs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-4">
                {loading ? "Loading call logs..." : "No call logs found"}
              </TableCell>
            </TableRow>
          ) : (
            callLogs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  {format(new Date(log.created_at), "MMM d, yyyy h:mm a")}
                </TableCell>
                <TableCell>
                  {(() => {
                    // Get phone number from various possible fields
                    const phoneNumber = 
                      (log.call_type === "inbound" ? log.caller_number : log.called_number) ||
                      log.to_number || 
                      log.from_number ||
                                      '';
                    
                    return phoneNumber ? <PhoneNumberDisplay phoneNumber={phoneNumber} /> : "-";
                  })()}
                </TableCell>
                <TableCell>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      log.call_type === "inbound"
                        ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                        : "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                    }`}
                  >
                    {log.call_type}
                  </span>
                </TableCell>
                <TableCell>{log.assistant_name || "N/A"}</TableCell>
                <TableCell>
                  {formatDuration(log.call_duration || 0)}
                </TableCell>
                <TableCell>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      log.status === "completed"
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                        : log.status === "failed"
                          ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                          : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                    }`}
                  >
                    {log.status}
                  </span>
                </TableCell>
                <TableCell>
                  <div
                    className="max-w-[150px] truncate"
                    title={log.ended_reason || ""}
                  >
                    {log.ended_reason || "-"}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    {log.transcript && log.transcript.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewTranscript(log)}
                        title="View Transcript"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    )}
                    {log.recording_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openMediaPlayer(log.recording_url!, log)}
                        title="Play Recording"
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <TranscriptDialog
        open={!!selectedTranscript}
        onOpenChange={(open) => {
          if (!open) setSelectedTranscript(null);
        }}
        transcript={selectedTranscript?.transcript || null}
        summary={selectedTranscript?.summary}
        callDate={selectedTranscript?.callDate}
        callInfo={selectedTranscript?.callInfo}
      />

      <Dialog
        open={!!selectedMedia}
        onOpenChange={(open) => {
          if (!open) setSelectedMedia(null);
        }}
      >
        <DialogContent className={selectedMedia?.isVideo ? "max-w-4xl" : "max-w-2xl"}>
          <DialogHeader>
            <DialogTitle>Call {selectedMedia?.isVideo ? 'Video' : 'Recording'}</DialogTitle>
            {selectedMedia?.callDate && (
              <DialogDescription>
                {format(new Date(selectedMedia.callDate), "MMMM d, yyyy h:mm a")}
                {selectedMedia.phoneNumber && (
                  <span className="ml-2">• {selectedMedia.phoneNumber}</span>
                )}
              </DialogDescription>
            )}
          </DialogHeader>
          {selectedMedia && (
            selectedMedia.isVideo ? (
              <VideoPlayer url={selectedMedia.url} className="w-full aspect-video" />
            ) : (
              <AudioPlayer url={selectedMedia.url} />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignSpecificCallLogsList;